import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/axios/api-service';
import { ENDPOINTS } from '../services/endpoints';
import { PAGINATION } from '../shared/appConstants';
import { getLocalDatePart } from '../shared/timeUtils';

export const fetchGanttData = createAsyncThunk(
  'calendar/fetchGanttData',
  async ({ projectId, fromDate, toDate, filters = {}, page = 1 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        limit: PAGINATION.DEFAULT_LIMIT.toString(),
        page: page.toString()
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });

      const response = await api.get(`${ENDPOINTS.CALENDAR_GANTT(projectId)}?${params.toString()}`);
      return response.data?.data || { tickets: [], pagination: {} };
    } catch (error) {
      const apiError = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to fetch gantt data';
      return rejectWithValue(apiError);
    }
  }
);

export const fetchCalendarData = createAsyncThunk(
  'calendar/fetchCalendarData',
  async ({ projectId, fromDate, toDate, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });

      const response = await api.get(`${ENDPOINTS.CALENDAR(projectId)}?${params.toString()}`);
      return response.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const createCalendarNote = createAsyncThunk(
  'calendar/createCalendarNote',
  async ({ projectId, text, assignee, ticket, note_date }, { rejectWithValue }) => {
    try {
      const body = { text, assignee, ticket, note_date };
      const response = await api.post(ENDPOINTS.CALENDAR_NOTE(projectId), body);
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create note');
    }
  }
);

export const updateCalendarNote = createAsyncThunk(
  'calendar/updateCalendarNote',
  async ({ noteId, text, assignee, ticket }, { rejectWithValue }) => {
    try {
      const body = {};
      if (text !== undefined) body.text = text;
      if (assignee !== undefined) body.assignee = assignee || null;
      if (ticket !== undefined) body.ticket = ticket || null;
      
      const response = await api.put(ENDPOINTS.CALENDAR_NOTE_UPDATE(noteId), body);
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update note');
    }
  }
);

export const deleteCalendarNote = createAsyncThunk(
  'calendar/deleteCalendarNote',
  async ({ noteId }, { rejectWithValue }) => {
    try {
      await api.delete(ENDPOINTS.CALENDAR_NOTE_DELETE(noteId));
      return noteId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to delete note');
    }
  }
);

const calendarSlice = createSlice({
  name: 'calendar',
  initialState: {
    calendarData: [],
    ganttData: { tickets: [], pagination: {} },
    ganttPagination: {
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 1
    },
    loading: false,
    ganttLoading: false,
    error: null,
    createNoteLoading: false,
    updateNoteLoading: false,
    deleteNoteLoading: false,
    viewType: 'Month',
    currentDate: new Date().toISOString(),
    customDates: [],
    ganttViewType: 'Week',
    ganttCurrentDate: new Date().toISOString(),
    ganttSelectedDates: (() => {
      const today = new Date();
      const dates = [];
      for (let i = 0; i < 4; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        dates.push(date.toISOString().split('T')[0]);
      }
      return dates;
    })(),
    ganttGroupByOption: 'taskType'
  },
  reducers: {
    clearCalendarData: (state) => {
      state.calendarData = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    setCalendarViewType: (state, action) => {
      state.viewType = action.payload;
    },
    setCalendarCurrentDate: (state, action) => {
      state.currentDate = action.payload;
    },
    setCalendarCustomDates: (state, action) => {
      state.customDates = action.payload;
    },
    setGanttViewType: (state, action) => {
      state.ganttViewType = action.payload;
    },
    setGanttCurrentDate: (state, action) => {
      state.ganttCurrentDate = action.payload;
    },
    setGanttSelectedDates: (state, action) => {
      state.ganttSelectedDates = action.payload;
    },
    setGanttPagination: (state, action) => {
      state.ganttPagination = action.payload;
    },
    setGanttGroupByOption: (state, action) => {
      state.ganttGroupByOption = action.payload;
    },
    addCalendarTicket: (state, action) => {
      const { date, ticket } = action.payload || {};
      const targetDate = date || getLocalDatePart(ticket?.start_date) || ticket?.startDate;

      if (!targetDate || !ticket?.id) return;

      const dayIndex = state.calendarData.findIndex(dayData => dayData.date === targetDate);

      if (dayIndex === -1) {
        state.calendarData.push({
          date: targetDate,
          tickets: [ticket],
          notes: []
        });
        state.calendarData.sort((a, b) => String(a.date).localeCompare(String(b.date)));
        return;
      }

      const existingTickets = state.calendarData[dayIndex].tickets || [];
      const alreadyExists = existingTickets.some(existingTicket => String(existingTicket.id) === String(ticket.id));

      if (!alreadyExists) {
        state.calendarData[dayIndex].tickets = [ticket, ...existingTickets];
      }
    },
    addCalendarNote: (state, action) => {
      const { date, note } = action.payload || {};
      const targetDate = date || note?.note_date || note?.date;

      if (!targetDate || !note?.id) return;

      const dayIndex = state.calendarData.findIndex(dayData => dayData.date === targetDate);

      if (dayIndex === -1) {
        state.calendarData.push({
          date: targetDate,
          tickets: [],
          notes: [note]
        });
        state.calendarData.sort((a, b) => String(a.date).localeCompare(String(b.date)));
        return;
      }

      const existingNotes = state.calendarData[dayIndex].notes || [];
      const alreadyExists = existingNotes.some(existingNote => String(existingNote.id) === String(note.id));

      if (!alreadyExists) {
        state.calendarData[dayIndex].notes = [note, ...existingNotes];
      }
    },
    replaceCalendarTicket: (state, action) => {
      const { ticketId, updates } = action.payload || {};
      if (!ticketId || !updates) return;

      state.calendarData = state.calendarData.map(dayData => ({
        ...dayData,
        tickets: (dayData.tickets || []).map(ticket =>
          String(ticket.id) === String(ticketId) ? { ...ticket, ...updates } : ticket
        )
      }));
    },
    replaceCalendarNote: (state, action) => {
      const { noteId, updates } = action.payload || {};
      if (!noteId || !updates) return;

      state.calendarData = state.calendarData.map(dayData => ({
        ...dayData,
        notes: (dayData.notes || []).map(note =>
          String(note.id) === String(noteId) ? { ...note, ...updates } : note
        )
      }));
    },
    removeCalendarTicket: (state, action) => {
      const ticketId = action.payload?.ticketId;
      if (!ticketId) return;

      state.calendarData = state.calendarData
        .map(dayData => ({
          ...dayData,
          tickets: (dayData.tickets || []).filter(ticket => String(ticket.id) !== String(ticketId))
        }))
        .filter(dayData => (dayData.tickets?.length || 0) > 0 || (dayData.notes?.length || 0) > 0);
    },
    removeCalendarNote: (state, action) => {
      const noteId = action.payload?.noteId;
      if (!noteId) return;

      state.calendarData = state.calendarData
        .map(dayData => ({
          ...dayData,
          notes: (dayData.notes || []).filter(note => String(note.id) !== String(noteId))
        }))
        .filter(dayData => (dayData.tickets?.length || 0) > 0 || (dayData.notes?.length || 0) > 0);
    },
    updateCalendarTicket: (state, action) => {
      const { ticketId, updates } = action.payload;
      if (!ticketId || !updates) return;

      const sourceDayIndex = state.calendarData.findIndex(dayData =>
        (dayData.tickets || []).some(ticket => String(ticket.id) === String(ticketId))
      );

      if (sourceDayIndex === -1) return;

      const sourceTickets = state.calendarData[sourceDayIndex].tickets || [];
      const sourceTicketIndex = sourceTickets.findIndex(ticket => String(ticket.id) === String(ticketId));

      if (sourceTicketIndex === -1) return;

      const currentTicket = sourceTickets[sourceTicketIndex];
      const updatedTicket = {
        ...currentTicket,
        ...(updates.completed !== undefined ? { marked_as_completed: updates.completed } : {}),
        ...updates
      };

      const currentDate = state.calendarData[sourceDayIndex].date;
      const nextDate =
        getLocalDatePart(updates.start_date) || updates.startDate ||
		    getLocalDatePart(updatedTicket.start_date) ||
        updatedTicket.startDate ||
        currentDate;

      if (!nextDate || nextDate === currentDate) {
        state.calendarData[sourceDayIndex].tickets[sourceTicketIndex] = updatedTicket;
        return;
      }

      state.calendarData[sourceDayIndex].tickets = sourceTickets.filter(
        ticket => String(ticket.id) !== String(ticketId)
      );

      if ((state.calendarData[sourceDayIndex].tickets?.length || 0) === 0 && (state.calendarData[sourceDayIndex].notes?.length || 0) === 0) {
        state.calendarData.splice(sourceDayIndex, 1);
      }

      const targetDayIndex = state.calendarData.findIndex(dayData => dayData.date === nextDate);

      if (targetDayIndex === -1) {
        state.calendarData.push({
          date: nextDate,
          tickets: [updatedTicket],
          notes: []
        });
        state.calendarData.sort((a, b) => String(a.date).localeCompare(String(b.date)));
        return;
      }

      const targetTickets = state.calendarData[targetDayIndex].tickets || [];
      const existingTargetIndex = targetTickets.findIndex(ticket => String(ticket.id) === String(ticketId));

      if (existingTargetIndex === -1) {
        state.calendarData[targetDayIndex].tickets = [updatedTicket, ...targetTickets];
      } else {
        state.calendarData[targetDayIndex].tickets[existingTargetIndex] = updatedTicket;
      }
    },
    updateGanttTicket: (state, action) => {
      const { ticketId, updates } = action.payload;
      const updateTaskRecursively = (tickets) => {
        return tickets.map(ticket => {
          if (String(ticket.id) === String(ticketId)) {
            return { ...ticket, ...updates };
          }

          if (ticket.subtasks && ticket.subtasks.length > 0) {
            const updatedSubtasks = updateTaskRecursively(ticket.subtasks);
            const subtaskWasUpdated = updatedSubtasks.some((subtask, index) => 
              subtask !== ticket.subtasks[index]
            );

            if (subtaskWasUpdated) {
              return { ...ticket, subtasks: updatedSubtasks };
            }
          }

          return ticket;
        });
      };

      state.ganttData = {
        ...state.ganttData,
        tickets: updateTaskRecursively(state.ganttData.tickets)
      };
    },
    addGanttTicketDependency: (state, action) => {
      const { ticketId, dependency } = action.payload;
      state.ganttData = {
        ...state.ganttData,
        tickets: state.ganttData.tickets.map(ticket => 
          ticket.id === ticketId 
            ? { ...ticket, dependencies: [...(ticket.dependencies || []), dependency] }
            : ticket
        )
      };
    },
    updateGanttTaskCompletion: (state, action) => {
      const { taskId, completed } = action.payload;
      const updates = {
        marked_as_completed: completed,
        completed: completed,
        is_completed: completed
      };
      
      // Helper function to update a task and recalculate parent progress
      const updateTaskRecursively = (tickets, targetTaskId, updates) => {
        return tickets.map(ticket => {
          // If this is the target task, update it
          if (ticket.id === targetTaskId) {
            return { ...ticket, ...updates };
          }
          
          // If this task has subtasks, check them recursively
          if (ticket.subtasks && ticket.subtasks.length > 0) {
            const updatedSubtasks = updateTaskRecursively(ticket.subtasks, targetTaskId, updates);
            
            // Check if any subtask was updated
            const subtaskWasUpdated = updatedSubtasks.some((subtask, index) => 
              subtask !== ticket.subtasks[index]
            );
            
            if (subtaskWasUpdated) {
              // Recalculate this parent task's completion based on subtasks
              const completedSubtasks = updatedSubtasks.filter(st => 
                st.completed || st.marked_as_completed || st.is_completed
              ).length;
              const totalSubtasks = updatedSubtasks.length;
              const allSubtasksCompleted = completedSubtasks === totalSubtasks;
              
              return {
                ...ticket,
                subtasks: updatedSubtasks,
                // Update parent completion status based on subtasks
                marked_as_completed: allSubtasksCompleted,
                completed: allSubtasksCompleted,
                is_completed: allSubtasksCompleted
              };
            }
          }
          
          return ticket;
        });
      };
      
      state.ganttData = {
        ...state.ganttData,
        tickets: updateTaskRecursively(state.ganttData.tickets, taskId, updates)
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGanttData.pending, (state) => {
        state.ganttLoading = true;
        state.error = null;
      })
      .addCase(fetchGanttData.fulfilled, (state, action) => {
        state.ganttLoading = false;
        state.ganttData = action.payload;
        // Update pagination state from API response
        if (action.payload.pagination) {
          state.ganttPagination = {
            currentPage: action.payload.pagination.current_page,
            pageSize: action.payload.pagination.page_size,
            totalCount: action.payload.pagination.total_count,
            totalPages: action.payload.pagination.total_pages
          };
        }
      })
      .addCase(fetchGanttData.rejected, (state, action) => {
        state.ganttLoading = false;
        state.error = action.payload || action.error.message;
        // Clear gantt data on error to show empty state instead of stale data
        state.ganttData = { tickets: [], pagination: {} };
        state.ganttPagination = {
          currentPage: 1,
          pageSize: 10,
          totalCount: 0,
          totalPages: 1
        };
      })
      .addCase(fetchCalendarData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalendarData.fulfilled, (state, action) => {
        state.loading = false;
        state.calendarData = action.payload;
      })
      .addCase(fetchCalendarData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(createCalendarNote.pending, (state) => {
        state.createNoteLoading = true;
        state.error = null;
      })
      .addCase(createCalendarNote.fulfilled, (state) => {
        state.createNoteLoading = false;
      })
      .addCase(createCalendarNote.rejected, (state, action) => {
        state.createNoteLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updateCalendarNote.pending, (state) => {
        state.updateNoteLoading = true;
        state.error = null;
      })
      .addCase(updateCalendarNote.fulfilled, (state) => {
        state.updateNoteLoading = false;
      })
      .addCase(updateCalendarNote.rejected, (state, action) => {
        state.updateNoteLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteCalendarNote.pending, (state) => {
        state.deleteNoteLoading = true;
        state.error = null;
      })
      .addCase(deleteCalendarNote.fulfilled, (state) => {
        state.deleteNoteLoading = false;
      })
      .addCase(deleteCalendarNote.rejected, (state, action) => {
        state.deleteNoteLoading = false;
        state.error = action.payload || action.error.message;
      });
  }
});

export const { clearCalendarData, clearError, setCalendarViewType, setCalendarCurrentDate, setCalendarCustomDates, setGanttViewType, setGanttCurrentDate, setGanttSelectedDates, setGanttPagination, setGanttGroupByOption, addCalendarTicket, addCalendarNote, replaceCalendarTicket, replaceCalendarNote, removeCalendarTicket, removeCalendarNote, updateCalendarTicket, updateGanttTicket, addGanttTicketDependency, updateGanttTaskCompletion } = calendarSlice.actions;
export default calendarSlice.reducer;
